import type { AxiosRequestConfig } from "axios";

import { apiClient } from "@/api";

export interface TResponse<T> {
  success: boolean;
  data: T;
  error: string | null;
}

export type RoleChat = "user" | "assistant";

export interface ChatStartRequest {
  sector?: string | null;
  value_chain?: string | null;
}

export interface ChatRequest {
  message: string;
  current_step: number;
  step_history?: Array<{ role: RoleChat; content: string }> | null;
  sector?: string | null;
  value_chain?: string | null;
}

export interface SectionScores {
  Agent_운영통제: number;
  PoC_실행_가능성: number;
  데이터_준비도: number;
  보안_규제_적합성: number;
  시스템_연동_가능성: number;
  업무_정의_수준: number;
  운영_확산_가능성: number;
  조직_의사결정_준비도: number;
}

export interface ChatMessage {
  id: string;
  role: RoleChat;
  content: string;
  timestamp: string;
}
export interface GenReportReq {
  project_name: string;
  chat_history: Array<{
    role: RoleChat;
    content: string;
  }>;
  section_scores: SectionScores;
}
export interface ChatResponse {
  reply: string;
  current_step: number;
  total_steps: number;
  is_completed: boolean;
  report: string | null;
}
export interface StartChatResponse {
  first_question: string;
  total_steps: number;
}

export interface ProjectReportResponse {
  project_id: string;
  reply: string;
  report: ProjectReport;
  status: string;
}

// ──────────────────────────────────────────────────────────
// 새 보고서 구조 타입
// ──────────────────────────────────────────────────────────

export interface ProjectReport {
  id: string;
  프로젝트명: string;
  요약: string;
  "문제 상황 정의": string;
  "예산 범위": BudgetRange;
  "1. 적용 업무 범위": ScopeDefinition;
  "2. 도입 목적": IntroductionPurpose;
  "3. 자동화 수준 정의": AutomationLevelDefinition;
  "4. 보안 및 시스템 연계 요구사항": SecurityAndIntegration;
  "5. 데이터 및 데이터 파이프라인 요건": DataPipelineRequirement;
  "6. Agent 적합성 필터 (요약)": AgentSuitabilityFilter;

  // Legacy support — old reports may still have this key
  "4. 보안 정책"?: SecurityPolicyLegacy;
}

interface BudgetRange {
  "투자 의향 예산": string | null;
}

interface ScopeDefinition {
  "In scope": string[];
}

interface IntroductionPurpose {
  "도입 목적": string;
}

interface AutomationLevelDetails {
  "Fully 자동화": string;
  "조건 기반 자동화": string;
}

interface AutomationLevelDefinition {
  "자동화 수준": string;
  "자동화 레벨 분류 및 세부 요건"?: AutomationLevelDetails;
  "Human-in-the-loop": string;
}

interface SystemIntegrationRequirements {
  "대상 시스템": string;
  "연계 방식 및 방향성": string;
  "데이터 지연 허용 범위": string;
}

interface SecurityAndIntegration {
  "시스템 구축 방식": string;
  "보안 정책": string;
  "시스템 연계 요구사항"?: SystemIntegrationRequirements;
}

// Legacy: old report format
export interface SecurityPolicyLegacy {
  "LLM 구축 방식": string;
  "보안 정책": string;
}

interface DataPipelineRequirement {
  "데이터 수집 및 정제 요구사항"?: string;
  "데이터 제약조건 및 검증 범위"?: string;
  // Legacy field
  "데이터 수집 및 정제"?: string;
}

interface AgentSuitabilityFilter {
  "적합 유형": string;
}

export const sendChatMessage = async (
  req: ChatRequest,
  config?: AxiosRequestConfig,
) => {
  return apiClient.post<TResponse<ChatResponse>>("/chat/message", req, config);
};

export const generateRfp = async (req: GenReportReq, config?: AxiosRequestConfig) => {
  return await apiClient.post<TResponse<ProjectReportResponse>>(`/chat/generate-rfp`, req, config);
};
